import {
  ForecastMapCapabilities,
  ForecastMapConfig,
  ForecastMapConfigCreateParams,
  ForecastMapConfigUpdateParams,
  ForecastMapExportResponse,
  ForecastMapPreview,
  ForecastMapPreviewCreateParams,
  ForecastMapRunManifest,
  ForecastMapRunStatusObject,
  TypedApiRequestParams,
} from '../types';

/*
Forecast maps consumer API (backend apps/forecast_map).
Mounted at /api/forecast/maps/ — configs + runs + previews DRF routers
plus the capabilities/ catalog view.

Viewer endpoints (list/detail/latest-run/export) are entitlement-gated;
composer endpoints (create/update/delete/fork/build/previews) require the
`forecast_map_builder` feature flag and answer 403 without it.
*/

const FORECAST_MAP_BASE = 'forecast/maps/';

type ForecastMapConfigFilter = {
  forecast_model?: string;
  /**
   * Also list the published presets this account cannot open, marked
   * `locked: true` (#327 MR7a): the unified map shows what Premium unlocks.
   * The list action only; detail and manifests of a locked preset 404.
   */
  include_locked?: true;
};

// Preset catalog. Free-tier users only receive presets flagged
// available_to_free_users -- unless `include_locked`, which adds the others
// marked `locked`; the endpoint is unpaginated (plain array).
const forecastMapConfigList = (
  filter?: ForecastMapConfigFilter,
): TypedApiRequestParams<ForecastMapConfig[]> => {
  const params: Record<string, string> = {};
  if (filter?.forecast_model) {
    params.forecast_model = filter.forecast_model;
  }
  // The backend reads exactly "1".
  if (filter?.include_locked) {
    params.include_locked = '1';
  }
  return {
    url: `${FORECAST_MAP_BASE}configs/`,
    method: 'GET',
    params: Object.keys(params).length > 0 ? params : undefined,
    authRequired: true,
  };
};

// `configId` may be the config's uuid: the detail route resolves either
// (the builder loads what its `?config=` names).
const forecastMapConfigDetail = (
  configId: number | string,
): TypedApiRequestParams<ForecastMapConfig> => ({
  url: `${FORECAST_MAP_BASE}configs/${configId}/`,
  method: 'GET',
  authRequired: true,
});

// Flipbook manifest for the latest COMPLETED run of a config.
// 404 (with a plain-string detail body) when no completed run exists yet.
const forecastMapLatestRun = (
  configId: number,
): TypedApiRequestParams<ForecastMapRunManifest> => ({
  url: `${FORECAST_MAP_BASE}configs/${configId}/runs/latest/`,
  method: 'GET',
  authRequired: true,
});

// Trigger (or return cached) MP4 for a completed run.
// 200 {status: 'completed', mp4_url} when the file already exists,
// 202 {status: 'building', mp4_url: null} when a build was enqueued,
// 409 {status: <run status>, mp4_url: null} when the run is not completed
// (surfaces as a rejected ApiError with statusCode 409).
const forecastMapRunExport = (
  runId: number,
): TypedApiRequestParams<ForecastMapExportResponse> => ({
  url: `${FORECAST_MAP_BASE}runs/${runId}/export/`,
  method: 'POST',
  authRequired: true,
});

// Composer capabilities catalog (auth only — no builder flag required;
// server-cached ~15 min).
const forecastMapCapabilities =
  (): TypedApiRequestParams<ForecastMapCapabilities> => ({
    url: `${FORECAST_MAP_BASE}capabilities/`,
    method: 'GET',
    authRequired: true,
  });

// Create a user map config → 201. 400 errors are keyed `name` (per-owner
// duplicate) or `parameters` (list of "<loc>: <msg>" schema messages);
// is_preset/auto_build/owner are ignored server-side.
const forecastMapConfigCreate = (
  params: ForecastMapConfigCreateParams,
): TypedApiRequestParams<ForecastMapConfig> => ({
  url: `${FORECAST_MAP_BASE}configs/`,
  method: 'POST',
  params,
  authRequired: true,
});

// Partial update of an own, non-preset config → 200 (404 for presets and
// foreign configs; same 400 error shapes as create).
const forecastMapConfigUpdate = (
  configId: number,
  params: ForecastMapConfigUpdateParams,
): TypedApiRequestParams<ForecastMapConfig> => ({
  url: `${FORECAST_MAP_BASE}configs/${configId}/`,
  method: 'PATCH',
  params,
  authRequired: true,
});

// Copy any visible config (presets included) into a new own config named
// "<src> (copy)" / "<src> (copy N)" → 201.
const forecastMapConfigFork = (
  configId: number,
): TypedApiRequestParams<ForecastMapConfig> => ({
  url: `${FORECAST_MAP_BASE}configs/${configId}/fork/`,
  method: 'POST',
  authRequired: true,
});

// Materialize a run of an own config against the latest completed forecast
// run. 202 run-status (build started) | 200 run-status (already completed)
// | 409 ForecastMapBuildConflict — {detail, run} while a build is in
// progress (show that run's progress) or {detail} when nothing is
// buildable yet. Failed runs are auto-deleted and rebuilt.
const forecastMapConfigBuild = (
  configId: number,
): TypedApiRequestParams<ForecastMapRunStatusObject> => ({
  url: `${FORECAST_MAP_BASE}configs/${configId}/build/`,
  method: 'POST',
  authRequired: true,
});

// Poll one run's status/progress (steps_completed / steps_total).
// Permission: entitlement OR builder flag; owned-config runs are visible
// to their owner.
const forecastMapRunDetail = (
  runId: number,
): TypedApiRequestParams<ForecastMapRunStatusObject> => ({
  url: `${FORECAST_MAP_BASE}runs/${runId}/`,
  method: 'GET',
  authRequired: true,
});

// Render a single preview frame of (possibly unsaved) parameters → 202
// pending preview. Same 400 `parameters` error shape as config create.
const forecastMapPreviewCreate = (
  params: ForecastMapPreviewCreateParams,
): TypedApiRequestParams<ForecastMapPreview> => ({
  url: `${FORECAST_MAP_BASE}previews/`,
  method: 'POST',
  params,
  authRequired: true,
});

// Poll a preview until status is completed (image_url set) or failed
// (error_detail set). Previews are ephemeral — never persist image_url.
const forecastMapPreviewDetail = (
  previewId: number,
): TypedApiRequestParams<ForecastMapPreview> => ({
  url: `${FORECAST_MAP_BASE}previews/${previewId}/`,
  method: 'GET',
  authRequired: true,
});

export default {
  forecastMapConfigList,
  forecastMapConfigDetail,
  forecastMapLatestRun,
  forecastMapRunExport,
  forecastMapCapabilities,
  forecastMapConfigCreate,
  forecastMapConfigUpdate,
  forecastMapConfigFork,
  forecastMapConfigBuild,
  forecastMapRunDetail,
  forecastMapPreviewCreate,
  forecastMapPreviewDetail,
};
